import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import { ReactNode, useRef, useState } from 'react';

import { SelectOptionProps } from '@/models/SelectOption';

export const SelectInput = ({
  id,
  label,
  value,
  selectOptions,
  hasError = false,
  disabled = false,
  required = false,
  onChange,
}: {
  id: string;
  label: ReactNode;
  value: string;
  selectOptions: SelectOptionProps[];
  hasError?: boolean;
  disabled?: boolean;
  required?: boolean;
  onChange: (e: SelectChangeEvent) => void;
}) => {
  const selectRef = useRef<HTMLInputElement | null>(null);
  const selectOptionWindowHeight = 200;
  const [menuPosition, setMenuPosition] = useState<{
    vertical: 'top' | 'bottom';
    transformOriginVertical: 'top' | 'bottom';
  }>({
    vertical: 'bottom',
    transformOriginVertical: 'top',
  });

  const handleOpen = () => {
    if (selectRef.current === null) return;

    const rect = selectRef.current.getBoundingClientRect();
    const windowHeight = window.innerHeight;

    if (windowHeight - rect.bottom < selectOptionWindowHeight) {
      // Adjust 200 based on dropdown height
      setMenuPosition({ vertical: 'top', transformOriginVertical: 'bottom' });
    } else {
      setMenuPosition({ vertical: 'bottom', transformOriginVertical: 'top' });
    }
  };

  return (
    <FormControl variant='outlined' error={hasError} required={required} fullWidth>
      <InputLabel id={`${id}-label`} htmlFor={id}>
        <Typography variant='subtitle2' component='span'>
          {label}
        </Typography>
      </InputLabel>
      <Select
        id={id}
        labelId={`${id}-label`}
        label={label}
        value={selectOptions != null && value != null ? value : ''}
        disabled={disabled}
        onChange={onChange}
        onOpen={handleOpen}
        ref={selectRef}
        MenuProps={{
          anchorOrigin: {
            vertical: menuPosition.vertical,
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: menuPosition.transformOriginVertical,
            horizontal: 'left',
          },
          PaperProps: {
            style: {
              maxHeight: `${selectOptionWindowHeight}px`,
              marginTop: menuPosition.vertical === 'bottom' ? '4px' : 0,
              marginBottom: menuPosition.vertical === 'top' ? '4px' : 0,
            },
          },
        }}
      >
        {(selectOptions ?? [])?.map((option: SelectOptionProps) => {
          return (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
};
